import React, { useEffect, useState } from 'react';
import { listClients } from '../../utils/clients';
import { formatMoney, formatQuoteDate } from '../../utils/quotes';
import { markPaid, isOverdue, KIND_LABELS } from '../../utils/invoices';
import { downloadInvoice } from '../../utils/invoicePdf';
import { MONO } from '../../constants/fonts';
import * as ds from '../../constants/dashboardStyles';

const styles = {
  card: ds.card,
  title: ds.cardTitle,
  description: ds.cardDescription,
  badge: ds.badge,
  empty: { ...ds.empty, margin: 0 },
  error: ds.error,
  total: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '14px 16px',
    marginBottom: 8,
    background: '#f9f9f7',
    border: '1px solid #ececE6',
    borderRadius: 10,
  },
  totalLabel: {
    fontSize: '0.8rem',
    color: '#777',
  },
  totalValue: {
    fontFamily: MONO,
    fontSize: '1.2rem',
    fontWeight: 700,
  },
  overdueText: {
    color: '#c0392b',
    fontWeight: 600,
  },
  clientBlock: {
    marginTop: 20,
  },
  clientHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 12,
    paddingBottom: 8,
    borderBottom: '1px solid #ececE6',
  },
  clientName: {
    fontFamily: MONO,
    fontSize: '0.88rem',
    fontWeight: 700,
    color: '#111',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  clientEmail: {
    fontSize: '0.75rem',
    color: '#888',
    fontWeight: 400,
  },
  clientTotal: {
    fontFamily: MONO,
    fontSize: '0.9rem',
    fontWeight: 700,
    flexShrink: 0,
  },
  // Wraps on narrow screens so the description keeps its width and the
  // controls drop below, rather than the text being squeezed to a column.
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    padding: '11px 0',
    borderBottom: '1px solid #f2f2ee',
    fontSize: '0.85rem',
  },
  main: {
    flex: '1 1 220px',
    minWidth: 0,
  },
  reference: {
    fontFamily: MONO,
    fontSize: '0.75rem',
    color: '#888',
  },
  meta: {
    fontSize: '0.75rem',
    color: '#888',
    marginTop: 2,
  },
  amount: {
    fontFamily: MONO,
    fontWeight: 700,
    flexShrink: 0,
  },
  smallButton: {
    padding: '5px 10px',
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#333',
    fontSize: '0.75rem',
    cursor: 'pointer',
    flexShrink: 0,
  },
};

const BADGE_COLORS = {
  unpaid: { background: '#fef3c7', color: '#92400e' },
  overdue: { background: '#fee2e2', color: '#991b1b' },
};

function daysOverdue(invoice) {
  if (!invoice.dueAt) return 0;
  return Math.floor((new Date() - invoice.dueAt.toDate()) / (1000 * 60 * 60 * 24));
}

// Every unpaid invoice across every client, so chasing payment doesn't mean
// clicking through clients one at a time. Deliberately has no client picker —
// the whole point is seeing everyone at once.
export default function OutstandingPanel({ invoices = [] }) {
  const [names, setNames] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    listClients()
      .then((clients) => setNames(Object.fromEntries(
        clients.map((client) => [client.email, client.name]),
      )))
      .catch(() => {});
  }, []);

  const unpaid = invoices.filter((invoice) => invoice.status !== 'paid');
  const total = unpaid.reduce((sum, invoice) => sum + (invoice.amount || 0), 0);
  const overdue = unpaid.filter(isOverdue);

  // Grouped by client, and ordered by who has been waiting longest — that's
  // the order you'd work down when chasing.
  const byClient = unpaid.reduce((acc, invoice) => {
    (acc[invoice.clientEmail] = acc[invoice.clientEmail] || []).push(invoice);
    return acc;
  }, {});

  const oldestIssue = (list) => Math.min(...list.map((i) => i.issuedAt?.seconds || Infinity));

  const groups = Object.entries(byClient)
    .map(([email, list]) => ({
      email,
      invoices: [...list].sort((a, b) => (a.issuedAt?.seconds || 0) - (b.issuedAt?.seconds || 0)),
      total: list.reduce((sum, i) => sum + (i.amount || 0), 0),
      oldest: oldestIssue(list),
    }))
    .sort((a, b) => a.oldest - b.oldest);

  return (
    <div style={styles.card}>
      <div style={styles.title}>Outstanding</div>
      <p style={styles.description}>Every unpaid invoice, oldest first. Whoever's been waiting longest is at the top.</p>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.total}>
        <span style={styles.totalLabel}>
          Owed to you
          {overdue.length > 0 && (
            <span style={styles.overdueText}> · {overdue.length} overdue</span>
          )}
        </span>
        <span style={styles.totalValue}>{formatMoney(total)}</span>
      </div>

      {unpaid.length === 0 && <p style={styles.empty}>Nothing outstanding — everything's been paid.</p>}

      {groups.map((group) => (
        <div key={group.email} style={styles.clientBlock}>
          <div style={styles.clientHeader}>
            <span style={styles.clientName}>
              {names[group.email] || group.email}
              {names[group.email] && <span style={styles.clientEmail}> · {group.email}</span>}
            </span>
            <span style={styles.clientTotal}>{formatMoney(group.total)}</span>
          </div>

          {group.invoices.map((invoice) => {
            const late = isOverdue(invoice);
            const days = late ? daysOverdue(invoice) : 0;
            return (
              <div key={invoice.id} style={styles.row}>
                <div style={styles.main}>
                  <div style={styles.reference}>{invoice.reference}</div>
                  <div>{invoice.description}</div>
                  <div style={{ ...styles.meta, ...(late ? styles.overdueText : {}) }}>
                    {KIND_LABELS[invoice.kind] || ''} · due {formatQuoteDate(invoice.dueAt) || ''}
                    {late && ` · ${days} day${days === 1 ? '' : 's'} overdue`}
                  </div>
                </div>
                <span style={styles.amount}>{formatMoney(invoice.amount)}</span>
                <span style={{ ...styles.badge, ...BADGE_COLORS[late ? 'overdue' : 'unpaid'] }}>
                  {late ? 'Overdue' : 'Unpaid'}
                </span>
                <button
                  type="button"
                  style={styles.smallButton}
                  onClick={() => markPaid(invoice.id, true).catch((e) => setError(e.message))}
                >
                  Mark paid
                </button>
                <button
                  type="button"
                  style={styles.smallButton}
                  onClick={() => downloadInvoice(invoice)}
                  title="Download PDF"
                >
                  ⤓
                </button>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
